import Config         from '@/core/misc/Config';
import EventBus        from '@/core/services/EventBus/EventBus';
import RpcService      from '@/core/services/Rpc/RpcService';
import SentryService   from '@/core/services/Sentry/SentryService';

/**
 * Сервис проверки версии сборки.
 *
 * Периодически опрашивает сервер (`versions.getLast`) и, если версия на сервере
 * отличается от собранной (`Config.app.buildId`), перезагружает страницу,
 * чтобы пользователь получил свежий бандл.
 *
 * Самодостаточен: в `init()` подписывается на ACL-события и сам управляет
 * запуском/остановкой интервала. Снаружи дёргается только `init()` из ядра.
 */
class BuildVersionChecker {
    /**
     * Интервал опроса сервера, мс
     */
    static readonly #checkInterval = 5 * 60 * 1000;

    /**
     * Имя query-параметра, которым сбрасывается кэш документа при перезагрузке.
     */
    static readonly #cacheBusterParam = 'v_refresh';

    /**
     * Ключ в sessionStorage, под которым хранится версия, ради которой уже
     * перезагружались. sessionStorage выбран намеренно: он переживает перезагрузку
     * и живёт в пределах вкладки, то есть ровно столько, сколько нужно.
     */
    static readonly #reloadedForKey = 'build_version_reloaded_for';

    /**
     * ID активного интервала. `null` — проверка не запущена.
     */
    static #intervalId: ReturnType<typeof setInterval> | null = null;

    /**
     * Инициализация сервиса.
     *
     * Вешает подписки на ACL-события: на логин и восстановление сессии
     * запускает периодическую проверку, на логаут и истечение сессии —
     * останавливает. Отписка не нужна — приложение единственное и живёт
     * весь жизненный цикл страницы.
     */
    static init(): void {
      EventBus.AclLogin.on(BuildVersionChecker.#start);
      EventBus.AclSessionRestored.on(BuildVersionChecker.#start);
      EventBus.AclLogout.on(BuildVersionChecker.#stop);
      EventBus.AclSessionExpired.on(BuildVersionChecker.#stop);
    }

    /**
     * Запустить периодическую проверку версии.
     *
     * В dev-окружении проверка не нужна — бандл собирается на лету.
     * Повторный запуск игнорируется, чтобы не плодить интервалы.
     *
     * Если в конфиг не подставился `buildId`, сравнивать не с чем: любая версия
     * с сервера будет считаться новой и уронит пользователя в цикл перезагрузок.
     * В этом случае проверку не запускаем и сообщаем об этом в Sentry.
     */
    static #start = (): void => {
      if (Config.environment === 'dev') {
        return;
      }

      if (BuildVersionChecker.#intervalId !== null) {
        return;
      }

      if (Config.app.buildId === undefined || Config.app.buildId === null || Config.app.buildId === '') {
        // #start синхронный, дождаться отправки нельзя — помечаем вызов как намеренно не ожидаемый
        void SentryService.reportError(new Error('Build version check disabled: buildId is empty'));
        return;
      }

      BuildVersionChecker.#intervalId = setInterval(() => {
        BuildVersionChecker.#checkCurrentBuild();
      }, BuildVersionChecker.#checkInterval);
    };

    /**
     * Остановить периодическую проверку версии.
     */
    static #stop = (): void => {
      if (BuildVersionChecker.#intervalId !== null) {
        clearInterval(BuildVersionChecker.#intervalId);
        BuildVersionChecker.#intervalId = null;
      } else {
        // Интервал и так не запущен — гасить нечего
      }
    };

    /**
     * Запросить версию с сервера и сравнить с собранной.
     *
     * Если версии не совпадают — фиксируем событие в Sentry и перезагружаем
     * страницу. Запрос фоновый: без лоадера и без тоста об ошибке
     * (Sentry-репорт при сбое запроса всё равно отправится сервисом RPC).
     *
     * Перед перезагрузкой запоминаем версию, ради которой перезагружаемся.
     * Если после перезагрузки версии всё ещё расходятся и целевая версия та же —
     * значит перезагрузка не дала свежий бандл. Повторять бесполезно: гасим
     * проверку и сообщаем об этом отдельным событием. Без этого пользователь
     * крутится в перезагрузках каждые пять минут, а с cache-buster ещё и по
     * новому URL на каждом круге.
     */
    static async #checkCurrentBuild(): Promise<void> {
      const response = await RpcService.makeRequestSilent('versions.getLast', {}, null, null, false);
      const data     = response.data;

      if (!data || !data.version) {
        return;
      }

      // Сравниваем версию с сервера с той, что зашита в конфиг при сборке.
      // Если не совпали — пользователю нужно получить свежий бандл.
      if (Config.app.buildId !== data.version) {
        if (BuildVersionChecker.#readReloadedFor() === data.version) {
          // Ради этой версии уже перезагружались, а buildId не изменился.
          // Перезагружать повторно — зациклить пользователя.
          console.log(`Перезагрузка не помогла: ${Config.app.buildId} -> ${data.version}`);

          await SentryService.reportError(
            new Error('Reload did not update the build'),
            { 'error.source': 'build_version_check' },
            { 'build_version': { buildId: Config.app.buildId, serverVersion: data.version } },
          );

          BuildVersionChecker.#stop();
        } else {
          console.log(`Обновление приложения: ${Config.app.buildId} -> ${data.version}`);

          BuildVersionChecker.#writeReloadedFor(data.version);

          // Ждём отправку до перезагрузки: иначе запрос обрывается уходом со страницы
          // и событие теряется (keepalive спасает не всегда, sendBeacon-фолбэк уже не отработает)
          await SentryService.reportError(new Error('New build detected, reloading'));

          BuildVersionChecker.#reloadWithCacheBuster();
        }
      } else {
        // Версии совпадают — приложение актуально.
        // Отметку о перезагрузке снимаем: она относилась к прошлому расхождению.
        BuildVersionChecker.#clearReloadedFor();
      }
    }

    /**
     * Перезагрузить страницу с гарантированным обходом кэша документа.
     *
     * Обычный `reload()` оставляет за браузером право отдать `index.html` из кэша.
     * Query-строка входит в ключ HTTP-кэша, поэтому URL с новым значением
     * `v_refresh` в кэше отсутствует заведомо, и браузер идёт на сервер.
     *
     * Используется `replace`, а не присваивание в `href`: перезагрузка не должна
     * плодить запись в истории, иначе кнопка «Назад» вернёт на тот же адрес
     * со старым значением параметра.
     */
    static #reloadWithCacheBuster(): void {
      const url = new URL(document.location.href);

      url.searchParams.set(BuildVersionChecker.#cacheBusterParam, String(Date.now()));

      document.location.replace(url.toString());
    }

    /**
     * Прочитать версию, ради которой уже перезагружались.
     *
     * sessionStorage может быть недоступен (приватный режим, политики браузера) —
     * в этом случае считаем, что отметки нет.
     */
    static #readReloadedFor(): string | null {
      try {
        return sessionStorage.getItem(BuildVersionChecker.#reloadedForKey);
      } catch (e) {
        return null;
      }
    }

    /**
     * Запомнить версию, ради которой перезагружаемся.
     */
    static #writeReloadedFor(version: string): void {
      try {
        sessionStorage.setItem(BuildVersionChecker.#reloadedForKey, version);
      } catch (e) {
        // Хранилище недоступно — защита от цикла не сработает,
        // но перезагрузку из-за этого блокировать не станем
      }
    }

    /**
     * Снять отметку о перезагрузке.
     */
    static #clearReloadedFor(): void {
      try {
        sessionStorage.removeItem(BuildVersionChecker.#reloadedForKey);
      } catch (e) {
        // Хранилище недоступно — снимать нечего
      }
    }
}

export default BuildVersionChecker;
